import React, { useRef, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { TableColumn } from 'react-data-table-component'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { Button } from '../../../components/Button/Button'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useNavigate } from 'react-router-dom'
import { TagElement } from '../../../components/Tag/Tag'
import { useGetScorecardsQuery } from '../../../store/ai/scorecard'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { CloneFormModal } from './CloneFormModal'
import { DeleteFormModal } from './DeleteFormModal'
import { Scorecard } from '../../../models/Scorecard'
import { SuccessToast } from '../../../components/SuccessToast/SuccessToast'
import { useLazyGetFeatureByIdQuery } from '../../../store/ai/ai'
import { SplitButton } from '../../../components/SplitButton/SplitButton'
import { clearImportedForm, setImportedForm } from '../../../store/formImport/importedForm'

const supportedFormImportType = 'application/json'

export const AIFormTab: React.FC = () => {
  useDocumentTitle('Setup')
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [showCloneFormModal, setShowCloneFormModal] = useState<boolean>(false)
  const [showDeleteFormModal, setShowDeleteFormModal] = useState<boolean>(false)
  const [showDownloadToast, setShowDownloadToast] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedForm, setSelectedForm] = useState<Scorecard | null>(null)
  const { hasQuota } = useZocksQuota()
  const formEnabled = hasQuota('ai.formFill.value')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: scorecardData, isFetching: isScorecardFetching } = useGetScorecardsQuery(
    {
      accountId: accountId,
      pageRequest: { page: page - 1, size: perPage },
      type: 'form',
    },
    { refetchOnMountOrArgChange: true },
  )

  const [getFeatureById] = useLazyGetFeatureByIdQuery()

  const handleShowForm = (form: Scorecard) => {
    navigate(`/workspace/setup/forms/form/details/${form.aiFeatureIds[0]}`)
  }

  const handleExportForm = async (form: Scorecard) => {
    const { aiFeatureIds, ...scorecardCreate } = form
    const { data: feature } = await getFeatureById({
      accountId: accountId,
      aiFeatureId: aiFeatureIds[0],
    })
    const integrationInfos = feature?.data.integrationInfos?.[0]?.fieldConfig ? feature.data.integrationInfos : null
    const formJson = JSON.stringify(
      {
        ...scorecardCreate,
        integrationInfos,
      },
      null,
      2,
    )

    const blob = new Blob([formJson], { type: 'application/json' })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${form.name.replace(/\s+/g, '_')}_form.json`
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    setSelectedForm(form)
    setShowDownloadToast(true)
  }

  const handleImportForm = () => {
    fileInputRef.current?.click()
  }

  const handleFileImport = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files ? evt.target.files[0] : null
    if (!file) {
      setErrorMessage('')
      return
    }

    if (supportedFormImportType !== file.type) {
      setErrorMessage(`Unsupported file type. Only JSON files are supported.`)
      return
    }
    const text = await file.text()

    try {
      const jsonContent = JSON.parse(text)
      if (jsonContent.name == null || jsonContent.description == null || jsonContent.type == null || jsonContent.descriptor == null) {
        setErrorMessage('Invalid form structure. The file must contain a valid form configuration.')
        return
      }

      console.log('Form to import:', jsonContent)
      dispatch(setImportedForm(jsonContent))
      navigate(`/workspace/setup/forms/form/create`)
    } catch (error) {
      console.error('Error parsing JSON:', error)
      setErrorMessage('Failed to parse the imported file. Please ensure it contains valid JSON.')
    }
  }

  const handleCloneForm = (form: Scorecard) => {
    setShowCloneFormModal(true)
    setSelectedForm(form)
  }

  const handleHideCloneForm = () => {
    setShowCloneFormModal(false)
  }

  const handleHideDeleteForm = () => {
    setShowDeleteFormModal(false)
  }

  const handleDeleteForm = (form: Scorecard) => {
    setShowDeleteFormModal(true)
    setSelectedForm(form)
  }

  const navigate = useNavigate()
  const handleNewForm = () => {
    dispatch(clearImportedForm())
    navigate(`/workspace/setup/forms/form/create`)
  }

  const columns: TableColumn<Scorecard>[] = [
    {
      name: 'Form Name',
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) => {
        return (
          <div className='flex-grow-1 d-flex gap-3 align-items-center'>
            <div className='button-link-medium text-decoration-none'>{row.name}</div>
          </div>
        )
      },
    },
    {
      name: 'Description',
      wrap: true,
      grow: 2,
      selector: (row) => row.description ?? '',
      sortable: true,
    },
    {
      name: 'Type',
      grow: 1,
      selector: () => 'System',
      sortable: true,
      cell: (row) => {
        return (
          <div className='d-flex gap-2 flex-wrap'>
            <TagElement>{row.systemPreset ? 'System' : row.isManagedByEnterprise ? 'Enterprise' : 'Custom'}</TagElement>
          </div>
        )
      },
    },
    {
      sortable: true,
      width: '1.5rem',
      button: true,
      cell: (row) => (
        <>
          <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Export form to file</Tooltip>}>
            {({ ref, ...triggerHandler }) => (
              <ScopedBlock
                missingScopeAction={MissingScopeAction.DISABLE}
                scope={{
                  scope: 'scorecard:read',
                  superScope: ['scorecard:admin'],
                }}
              >
                <Button className='p-0' onClick={() => handleExportForm(row)} {...triggerHandler}>
                  <i ref={ref} className='bi bi-download' />
                </Button>
              </ScopedBlock>
            )}
          </OverlayTrigger>
        </>
      ),
    },
    {
      sortable: true,
      width: '1.5rem',
      button: true,
      cell: (row) => (
        <>
          <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Clone form</Tooltip>}>
            {({ ref, ...triggerHandler }) => (
              <ScopedBlock
                missingScopeAction={MissingScopeAction.DISABLE}
                scope={{
                  scope: 'scorecard:create',
                  superScope: ['scorecard:admin'],
                }}
              >
                <Button className='p-0' onClick={() => handleCloneForm(row)} {...triggerHandler}>
                  <i ref={ref} className='bi bi-copy' />
                </Button>
              </ScopedBlock>
            )}
          </OverlayTrigger>
        </>
      ),
    },
    {
      sortable: true,
      width: '1.5rem',
      button: true,
      cell: (row) => {
        return (
          <>
            <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Customize form</Tooltip>}>
              {({ ref, ...triggerHandler }) => (
                <ScopedBlock
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{
                    scope: 'scorecard:list',
                    superScope: ['scorecard:admin'],
                  }}
                >
                  <Button className='p-0' onClick={() => handleShowForm(row)} {...triggerHandler}>
                    <i ref={ref} className='bi bi-gear' />
                  </Button>
                </ScopedBlock>
              )}
            </OverlayTrigger>
          </>
        )
      },
    },
    {
      sortable: true,
      width: '1.5rem',
      button: true,
      cell: (row) => {
        return (
          <>
            <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Delete form</Tooltip>}>
              {({ ref, ...triggerHandler }) => (
                <ScopedBlock
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{
                    scope: 'scorecard:delete',
                    superScope: ['scorecard:admin'],
                  }}
                >
                  <Button className='p-0' onClick={() => handleDeleteForm(row)} {...triggerHandler} disabled={row.systemPreset || row.isManagedByEnterprise}>
                    <i ref={ref} className='bi bi-trash' />
                  </Button>
                </ScopedBlock>
              )}
            </OverlayTrigger>
          </>
        )
      },
    },
  ]

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  return (
    <Container fluid>
      <Form.Control ref={fileInputRef} id='form-file' type='file' accept={supportedFormImportType} onChange={handleFileImport} hidden={true} />
      {showCloneFormModal && <CloneFormModal show={showCloneFormModal} onHide={handleHideCloneForm} form={selectedForm} />}
      {showDeleteFormModal && <DeleteFormModal show={showDeleteFormModal} onHide={handleHideDeleteForm} form={selectedForm} />}
      <SuccessToast
        position={'bottom-center'}
        message={`Form "${selectedForm?.name}" downloaded.`}
        show={showDownloadToast}
        onClose={() => setShowDownloadToast(false)}
      />
      <Row className='mb-4'>
        <Col>
          <ScopedBlock
            scope={{
              scope: 'scorecard:create',
              superScope: ['scorecard:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <ScopedBlock
              quota={{
                quota: 'ai.formFill.value',
              }}
              missingScopeAction={MissingScopeAction.UPGRADE}
            >
              <div className='d-flex gap-2'>
                <SplitButton
                  variant='primary'
                  label={
                    <>
                      <i className='bi bi-plus' />
                      Add new form
                    </>
                  }
                  onClick={handleNewForm}
                >
                  <Button className='px-2 py-1 flex-grow-1' variant='primary' title='Import form from file' onClick={() => handleImportForm()}>
                    Import form
                  </Button>
                </SplitButton>
              </div>
            </ScopedBlock>
          </ScopedBlock>
        </Col>
      </Row>
      <Row>
        <span className='text-danger'>{errorMessage}</span>
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            disabled={!formEnabled}
            columns={columns}
            data={scorecardData?.data ?? []}
            progressPending={isScorecardFetching}
            pagination
            paginationServer
            paginationTotalRows={scorecardData?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
